import {
  TIPOS_OBJETO_TABLETOP,
  IDS_PORTAS_TABLETOP,
  CORES_TRIAGEM,
  MATERIAIS_ABSORVENTES,
  type CenaTabletop,
  type ObjetoTabletop,
} from './tipos'
import { copiarCenaTabletop } from './operacoes'
import { migrarCenaV1ParaV2, ehCenaV1 } from './migracao'

const tiposConhecidos = new Set<string>(TIPOS_OBJETO_TABLETOP)
const portasConhecidas = new Set<string>(IDS_PORTAS_TABLETOP)
const materiaisConhecidos = new Set<string>(MATERIAIS_ABSORVENTES)
const coresTriagemConhecidas = new Set<string>(CORES_TRIAGEM)

const ehRegistro = (valor: unknown): valor is Record<string, unknown> =>
  typeof valor === 'object' && valor !== null && !Array.isArray(valor)

const ehStringNaoVazia = (valor: unknown): valor is string =>
  typeof valor === 'string' && valor.trim().length > 0

const ehVetorFinito = (valor: unknown): valor is readonly [number, number, number] =>
  Array.isArray(valor) &&
  valor.length === 3 &&
  valor.every((coordenada) => typeof coordenada === 'number' && Number.isFinite(coordenada))

const ehEscalaPositiva = (valor: unknown): valor is readonly [number, number, number] =>
  ehVetorFinito(valor) && valor.every((componente) => componente > 0)

/** Só confere que o vínculo aponta para algo plausível; a existência do alvo é responsabilidade do chamador. */
const ehLocalVitimaValido = (valor: unknown): boolean => {
  if (!ehRegistro(valor)) return false
  if (valor.tipo === 'solo') return ehStringNaoVazia(valor.postura)
  if (valor.tipo === 'veiculo') return ehStringNaoVazia(valor.idVeiculo) && ehStringNaoVazia(valor.idAncora)
  return false
}

const ehBlocoRiscoValido = (valor: unknown): boolean =>
  ehRegistro(valor) &&
  (valor.vazamentoAtivo === undefined || typeof valor.vazamentoAtivo === 'boolean') &&
  (valor.materiaisAceitos === undefined ||
    (Array.isArray(valor.materiaisAceitos) &&
      valor.materiaisAceitos.every((m) => typeof m === 'string' && materiaisConhecidos.has(m))))

const ehBlocoVitimaValido = (valor: unknown): boolean =>
  ehRegistro(valor) &&
  ehStringNaoVazia(valor.idPerfil) &&
  typeof valor.triagem === 'string' &&
  coresTriagemConhecidas.has(valor.triagem) &&
  typeof valor.colarCervical === 'boolean' &&
  ehLocalVitimaValido(valor.local) &&
  // vínculo com a ficha clínica: opcional (cenas antigas não têm), mas quando
  // existe precisa ser um id utilizável — é o que a sessão usa para achar a ficha
  (valor.fichaAtendimentoId === undefined || ehStringNaoVazia(valor.fichaAtendimentoId))

const ehBlocoVeiculoValido = (valor: unknown): boolean => {
  if (!ehRegistro(valor)) return false
  if (!ehStringNaoVazia(valor.idPerfil) || !ehStringNaoVazia(valor.posicaoFinal)) return false
  if (!ehRegistro(valor.estadosPecas) || !ehRegistro(valor.vidros) || !ehRegistro(valor.protecoes)) return false
  if (typeof valor.insulfilm !== 'boolean') return false
  if (valor.deformacao !== undefined && valor.deformacao !== null) {
    if (!ehRegistro(valor.deformacao)) return false
    if (!ehStringNaoVazia(valor.deformacao.regiao) || !ehStringNaoVazia(valor.deformacao.nivel)) return false
    if (typeof valor.deformacao.intrusaoM !== 'number' || !Number.isFinite(valor.deformacao.intrusaoM)) return false
  }
  if (valor.deformacoes !== undefined) {
    if (!Array.isArray(valor.deformacoes)) return false
    for (const d of valor.deformacoes) {
      if (!ehRegistro(d) || !ehStringNaoVazia(d.regiao) || !ehStringNaoVazia(d.nivel)) return false
      if (typeof d.intrusaoM !== 'number' || !Number.isFinite(d.intrusaoM) || d.intrusaoM < 0) return false
      if (d.idAmassado !== undefined && (typeof d.idAmassado !== 'string' || !d.idAmassado.trim())) return false
      if (d.centroLongitudinalM !== undefined && (typeof d.centroLongitudinalM !== 'number' || !Number.isFinite(d.centroLongitudinalM))) return false
      if (d.idPorta !== undefined && (
        typeof d.idPorta !== 'string' ||
        !portasConhecidas.has(d.idPorta) ||
        d.regiao !== (d.idPorta.endsWith('Esquerda') ? 'lateralEsquerda' : 'lateralDireita')
      )) return false
      if (d.centroFaceM !== undefined && (typeof d.centroFaceM !== 'number' || !Number.isFinite(d.centroFaceM))) return false
      if (d.alturaContatoY !== undefined && (typeof d.alturaContatoY !== 'number' || !Number.isFinite(d.alturaContatoY))) return false
    }
  }
  if (valor.cortes !== undefined) {
    if (!Array.isArray(valor.cortes)) return false
    for (const c of valor.cortes) {
      if (!ehRegistro(c) || !ehStringNaoVazia(c.idPeca) || !ehStringNaoVazia(c.estado)) return false
    }
  }
  return true
}

const ehObjetoTabletop = (valor: unknown): valor is ObjetoTabletop => {
  if (!ehRegistro(valor) || !ehRegistro(valor.transformacao)) return false
  const base =
    ehStringNaoVazia(valor.id) &&
    ehStringNaoVazia(valor.nome) &&
    typeof valor.tipo === 'string' &&
    tiposConhecidos.has(valor.tipo) &&
    (valor.cor === undefined || typeof valor.cor === 'string') &&
    ehVetorFinito(valor.transformacao.posicao) &&
    ehVetorFinito(valor.transformacao.rotacao) &&
    ehEscalaPositiva(valor.transformacao.escala)
  if (!base) return false
  if (valor.vitima !== undefined && !ehBlocoVitimaValido(valor.vitima)) return false
  if (valor.veiculo !== undefined && !ehBlocoVeiculoValido(valor.veiculo)) return false
  if (valor.risco !== undefined && !ehBlocoRiscoValido(valor.risco)) return false
  return true
}

/**
 * Vínculos consistentes entre os objetos da cena: toda vítima "no veículo"
 * aponta para um `idVeiculo` que existe, e no máximo uma vítima ocupa cada
 * âncora daquele veículo — mas nada impede várias vítimas no MESMO veículo
 * em âncoras diferentes.
 */
const vinculosConsistentes = (objetos: readonly ObjetoTabletop[]): boolean => {
  const idsVeiculo = new Set(objetos.filter((o) => o.veiculo).map((o) => o.id))
  const ocupacoes = new Set<string>()
  for (const objeto of objetos) {
    const local = objeto.vitima?.local
    if (!local || local.tipo !== 'veiculo') continue
    if (!idsVeiculo.has(local.idVeiculo)) return false
    const chave = `${local.idVeiculo}::${local.idAncora}`
    if (ocupacoes.has(chave)) return false
    ocupacoes.add(chave)
  }
  return true
}

const ehItemAcionamentoValido = (valor: unknown): boolean =>
  ehRegistro(valor) &&
  ehStringNaoVazia(valor.id) &&
  typeof valor.texto === 'string' &&
  typeof valor.confirmado === 'boolean' &&
  typeof valor.visivel === 'boolean'

const ehAcionamentoValido = (valor: unknown): boolean =>
  ehRegistro(valor) &&
  typeof valor.natureza === 'string' &&
  typeof valor.local === 'string' &&
  typeof valor.horario === 'string' &&
  typeof valor.recursos === 'string' &&
  Array.isArray(valor.informacoes) &&
  valor.informacoes.every(ehItemAcionamentoValido)

export function validarCenaTabletop(valor: unknown): valor is CenaTabletop {
  if (
    !ehRegistro(valor) ||
    valor.versao !== 2 ||
    typeof valor.nome !== 'string' ||
    (valor.historiaInicial !== undefined && typeof valor.historiaInicial !== 'string') ||
    !Array.isArray(valor.objetos) ||
    !valor.objetos.every(ehObjetoTabletop) ||
    (valor.acionamento !== undefined && !ehAcionamentoValido(valor.acionamento))
  ) {
    return false
  }

  const ids = valor.objetos.map((objeto) => objeto.id)
  return new Set(ids).size === ids.length && vinculosConsistentes(valor.objetos)
}

export function serializarCenaTabletop(cena: CenaTabletop): string {
  if (!validarCenaTabletop(cena)) {
    throw new Error('Arquivo de table top inválido: a cena não pode ser serializada.')
  }
  return JSON.stringify(cena, null, 2)
}

/**
 * Aceita tanto o formato atual (v2) quanto arquivos exportados pelo antigo
 * Diorama 3D (v1, `versao: 1`), migrando-os automaticamente.
 */
export function desserializarCenaTabletop(json: string): CenaTabletop {
  let valor: unknown
  try {
    valor = JSON.parse(json) as unknown
  } catch {
    throw new Error('Arquivo de table top inválido: o conteúdo não é um JSON válido.')
  }

  if (ehCenaV1(valor)) {
    return migrarCenaV1ParaV2(valor)
  }

  if (!validarCenaTabletop(valor)) {
    throw new Error('Arquivo de table top inválido: estrutura ou valores não reconhecidos.')
  }

  return copiarCenaTabletop(valor)
}

